import React, { useEffect, useState } from "react";
import {
  Table,
  Tabs,
  Button,
  Tag,
  message,
  Spin,
  Select,
  Input,
  Modal,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllClasses } from "../../../Store/Slices/Admin/Class/actions/classThunk";
import AdminDashLayout from "../../../Components/Admin/AdminDashLayout";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../Components/Common/Sidebar";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../config/permission";
import {
  fetchApplicationRequests,
  updateApplicationRequest,
} from "../../../Store/Slices/Admin/ApplicationRequests/applicationRequestsThunk";
import useNavHeading from "../../../Hooks/CommonHooks/useNavHeading ";
import RequestDetails from "./RequestDetails";
import TableShimmer from "../../../Components/Common/TableShimmer";

const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

const ManagerApproval = () => {
  const { t } = useTranslation("common");
  useNavHeading(t("Benifit"), t("Manager Approval"));
  const dispatch = useDispatch();
  

  const { applicationRequestsData, loading, totalRecords } = useSelector(
    (state) => state.admin.applicationRequests
  );

  const { userDetails } = useSelector((store) => store.common.user);
  const [statusFilter, setStatusFilter] = useState(""); // all, pending, approved, rejected
  const [applicantType, setApplicantType] = useState(null);
  const [classId, setClassId] = useState(null);
  const [sectionId, setSectionId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const [newStatus, setNewStatus] = useState("");


  useEffect(() => {
    dispatch(fetchAllClasses());
  }, [dispatch]);


  // Fetch requests based on filters
  useEffect(() => {
    dispatch(
      fetchApplicationRequests({
        status: statusFilter || undefined,
        applicantType,
        applicationType: "benifit",
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        page,
        limit,
        search: searchText,
      })
    );
  }, [
    dispatch,
    statusFilter,
    applicantType,
    classId,
    sectionId,
    page,
    limit,
    searchText,
  ]);

  const reloadData = () => {
    dispatch(
      fetchApplicationRequests({
        status: statusFilter || undefined,
        applicantType,
        applicationType: "benifit",
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        page,
        limit,
        search: searchText,
      })
    );
  };

  // Approve handler
  const handleApprove = (id) => {
    let remarks;
    Modal.confirm({
      title: t("Are you sure you want to approve this application?"),
      content: (
        <Input.TextArea
          placeholder="Enter remark (optional)"
          onChange={(e) => (remarks = e.target.value)}
          rows={3}
        />
      ),
      okButtonProps: {
        className: "btn-gradient  ",
      },
      onOk: async () => {
        if (!remarks?.trim()) {
          message.error("remark is required to reject the application.");
          return;
        }
        try {
          const res = await dispatch(
            updateApplicationRequest({
              applicationId: id,
              status: "approved",
              staffId: userDetails?.userId,
              remarks,
            })
          );
          if (res?.payload?.success) {
            message.success(res?.payload?.message || "Application Approved");
            reloadData();
          } else {
            message.error("Approval Failed");
          }
        } catch {
          message.error(t("Failed to approve application."));
        }
      },
    });
  };

  // Reject handler
  const handleReject = (id) => {
    let remarks;
    Modal.confirm({
      title: t("Are you sure you want to reject this application?"),
      content: (
        <Input.TextArea
          placeholder="Enter remark (optional)"
          onChange={(e) => (remarks = e.target.value)}
          rows={3}
        />
      ),
      okButtonProps: {
        className: "btn-gradient  ",
      },
      onOk: async () => {
        if (!remarks?.trim()) {
          message.error("remark is required to reject the application.");
          return;
        }
        try {
          const res = await dispatch(
            updateApplicationRequest({
              applicationId: id,
              status: "rejected",
              staffId: userDetails?.userId,
              remarks,
            })
          );
          if (res?.payload?.success) {
            message.success(res?.payload?.message || "Application Rejected");
            reloadData();
          } else {
            message.error("Rejection Failed");
          }
        } catch {
          message.error("Failed to reject application.");
        }
      },
    });
  };

  // Change status modal
  const handleChangeStatus = (id) => {
    const app = applicationRequestsData.find((item) => item._id === id);
    setNewStatus(app ? app.applicationStatus : "");
    setCurrentApplicationId(id);
    setStatusModalVisible(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      message.warning(t("Please select a status."));
      return;
    }
    try {
      const res = await dispatch(
        updateApplicationRequest({
          applicationId: currentApplicationId,
          status: newStatus,
          staffId: userDetails?.userId,
        })
      );
      if (res?.payload?.success) {
        message.success(res?.payload?.message || "Status updated.");
        setStatusModalVisible(false);
        setNewStatus("");
        reloadData();
      } else {
        message.error("Failed to update status.");
      }
    } catch {
      message.error(t("Failed to update application status."));
    }
  };

  // Table Columns
  const columns = [
    {
      title: t("Request ID"),
      dataIndex: "_id",
      render: (_, record, index) => `REQ-${record.requestCode || index + 1}`,
    },
    {
      title: t("Category"),
      dataIndex: "applicationType",
      render: (type) => (
        <Tag color="blue" className="w-20 text-center">
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t("Subject"),
      dataIndex: "subject",
    },
    {
      title: t("Status"),
      dataIndex: "applicationStatus",
      render: (status) => {
        const colorMap = {
          approved: "green",
          rejected: "red",
          pending: "orange",
        };
        return (
          <Tag
            color={colorMap[status] || "default"}
            className="w-20 text-center"
          >
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: t("Last Updated"),
      dataIndex: "updatedAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: t("Action"),
      key: "actions",
      render: (_, record) => (
        <div className="flex flex-col items-start">
          <Button
            type="text"
            size="small"
            className="text-purple-700 text-start"
            onClick={() => {
              setSelectedApplication(record);
              setDetailsDrawerOpen(true);
            }}
          >
            {t("Details")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AdminDashLayout>
      <div className="p-6 bg-white rounded-lg  space-y-4">
        {/* Status Tabs */}
        <Tabs
          activeKey={statusFilter}
          onChange={(key) => {
            setStatusFilter(key);
            setPage(1);
          }}
        >
          <TabPane tab={t("All")} key="" />
          <TabPane tab={t("Pending")} key="pending" />
          <TabPane tab={t("Approved")} key="approved" />
          <TabPane tab={t("Rejected")} key="rejected" />
        </Tabs>

        {/* <div className="flex gap-4 mb-4">
          <Select
            placeholder={t("Applicant Type")}
            value={applicantType}
            onChange={(value) => {
              setApplicantType(value);
              setPage(1);
            }}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="student">{t("Student")}</Option>
            <Option value="staff">{t("Staff")}</Option>
          </Select>

          <Search
            placeholder={t("Search by Subject / Description")}
            onSearch={(value) => {
              setSearchText(value);
              setPage(1);
            }}
            style={{ width: 250 }}
            allowClear
          />
        </div> */}

        {loading ? (
          <div className="">
            <TableShimmer columns={6} />
          </div>
        ) : (
          <Table
            dataSource={applicationRequestsData}
            columns={columns}
            rowKey="_id"
            pagination={{
              current: page,
              pageSize: limit,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setLimit(newPageSize);
              },
            }}
          />
        )}

        {/* Status Modal */}
        <Modal
          title={t("Change Application Status")}
          open={statusModalVisible}
          onCancel={() => {
            setStatusModalVisible(false);
            setNewStatus("");
          }}
          onOk={handleStatusUpdate}
          okText={t("Update")}
        >
          <Select
            value={newStatus}
            onChange={(value) => setNewStatus(value)}
            placeholder={t("Select status")}
            style={{ width: "100%" }}
          >
            <Option value="pending">{t("Pending")}</Option>
            <Option value="approved">{t("Approved")}</Option>
            <Option value="rejected">{t("Rejected")}</Option>
          </Select>
        </Modal>
      </div>

      {/* Details Sidebar */}
      <Sidebar
        isOpen={detailsDrawerOpen}
        onClose={() => {
          setDetailsDrawerOpen(false);
          setSelectedApplication(null);
        }}
        title={t("Request Details")}
        width={"70%"}
      >
        <RequestDetails data={selectedApplication} />
      </Sidebar>
    </AdminDashLayout>
  );
};

export default ManagerApproval;
